import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, LogOut, RefreshCw, RotateCw } from 'lucide-react'
import { useI18n } from '../i18n'
import { useAuth } from '../auth'
import { kaufwegSichtbar } from '../lib/kaufweg'
import { darf } from '../lib/rollen'
import { formatieren, istGueltig, normalisieren } from '../lib/steuernummer'
import { useStore } from '../data/store'
import type { Database } from '../data/model'
import { Button, Card, Field, Input, Select, cx } from '../ui/kit'
import StripeConnectCard from './StripeConnectCard'
import LogoCard from './LogoCard'
import ChildRatesCard from './ChildRatesCard'
import CancellationTermsCard from './CancellationTermsCard'
import IcalCard from './IcalCard'
import AiUsageCard from './AiUsageCard'
import { ComplianceDemoNote, RksvCard, TouristTaxFilingCard } from './ComplianceCards'
import { KontoLoeschenEintrag } from './KontoLoeschung'
import { AboKuendigenEintrag } from './AboKuendigung'
import AvvNachweise from './AvvNachweise'
import { RatesCard } from './RatesEditor'
import { UnitsCard } from './UnitsEditor'
import { oeffentlicheUrl } from '../lib/urls'

/**
 * Issuer details. Every field here lands on a quote or an invoice, and an invoice
 * without an address, a VAT id and an account number is not a valid one — so this
 * is the first thing a new landlord has to fill in.
 */
function IssuerCard() {
  const { t, lang } = useI18n()
  const store = useStore()
  const de = lang === 'de'
  const [draft, setDraft] = useState(store.db.tenant)
  const [saved, setSaved] = useState(false)
  const [taxNumberBad, setTaxNumberBad] = useState(false)

  // Pick up the tenant once it has loaded from the server.
  useEffect(() => { setDraft(store.db.tenant) }, [store.db.tenant])

  const set = (patch: Partial<Database['tenant']>) => {
    setDraft({ ...draft, ...patch })
    setSaved(false)
  }

  const missing = !draft.street || !draft.city || !draft.bank_iban
  const save = async () => {
    /*
     * Die Steuernummer wird vor dem Speichern geprüft, nicht danach von der
     * Datenbank abgewiesen: `tenants_tax_number_chk` würde den GANZEN
     * Speichervorgang scheitern lassen, und der Vermieter verlöre auch die
     * übrigen Eingaben. Eine formal ungültige Nummer wird mit Hinweis
     * abgewiesen, nie stillschweigend übernommen oder verstümmelt (AK8).
     * Gespeichert wird normalisiert (neun Ziffern), angezeigt XX-XXX/XXXX.
     */
    const steuernummer = (draft.tax_number ?? '').trim()
    if (steuernummer !== '' && !istGueltig(steuernummer)) {
      setTaxNumberBad(true)
      return
    }
    setTaxNumberBad(false)
    await store.saveTenant({
      name: draft.name,
      legal_name: draft.legal_name,
      // The one-line `address` is composed from these by a trigger; writing it here
      // as well would just race the trigger.
      street: draft.street,
      postcode: draft.postcode,
      city: draft.city,
      vat_id: draft.vat_id,
      tax_number: steuernummer === '' ? null : normalisieren(steuernummer),
      company_register: draft.company_register,
      company_register_court: draft.company_register_court,
      email: draft.email,
      phone: draft.phone,
      bank_holder: draft.bank_holder,
      bank_iban: draft.bank_iban,
      bank_bic: draft.bank_bic,
      vat_scheme: draft.vat_scheme,
      vat_rate_accommodation: draft.vat_rate_accommodation,
      vat_rate_services: draft.vat_rate_services,
      prices_include_vat: draft.prices_include_vat,
      prepayment_mode: draft.prepayment_mode,
      prepayment_percent: draft.prepayment_percent,
      prepayment_fixed: draft.prepayment_fixed,
    })
    setSaved(true)
  }

  return (
    <Card className="p-4 space-y-3">
      <h2 className="font-medium">
        {de ? 'Rechnungsangaben' : 'Invoice details'}
      </h2>
      {missing && (
        <p className="text-sm bg-warn-soft text-warn rounded-xl px-3 py-2">
          {de
            ? 'Adresse und IBAN fehlen — ohne sie ist eine Rechnung nicht vollständig.'
            : 'Address and IBAN are missing — an invoice is not complete without them.'}
        </p>
      )}

      <Field label={de ? 'Anzeigename' : 'Display name'}>
        <Input value={draft.name ?? ''} onChange={(e) => set({ name: e.target.value })} />
      </Field>
      <Field
        label={de ? 'Firmenwortlaut' : 'Legal name'}
        hint={de ? 'Erscheint auf Dokumenten, falls abweichend' : 'Used on documents if different'}
      >
        <Input value={draft.legal_name ?? ''}
          onChange={(e) => set({ legal_name: e.target.value })} />
      </Field>
      <Field label={de ? 'Straße und Hausnummer' : 'Street and number'}>
        <Input value={draft.street ?? ''}
          placeholder={de ? 'Dorfstraße 12' : 'Main Street 12'}
          autoComplete="street-address"
          onChange={(e) => set({ street: e.target.value })} />
      </Field>
      <div className="grid grid-cols-[7rem_1fr] gap-3">
        <Field label={de ? 'PLZ' : 'Postcode'}>
          <Input value={draft.postcode ?? ''}
            placeholder="6450"
            inputMode="numeric"
            autoComplete="postal-code"
            onChange={(e) => set({ postcode: e.target.value })} />
        </Field>
        <Field label={de ? 'Ort' : 'Town'}>
          <Input value={draft.city ?? ''}
            placeholder="Sölden"
            autoComplete="address-level2"
            onChange={(e) => set({ city: e.target.value })} />
        </Field>
      </div>

      {/*
        Firmenbuchnummer und Gericht sind PFLICHTANGABEN in geschäftlichen E-Mails
        eingetragener Unternehmen (§ 14 UGB in Österreich, § 35a GmbHG in
        Deutschland) — nicht nur im Impressum, sondern in jeder Mail, die aus dem
        Betrieb hinausgeht. StaySuite verschickt Bestätigungen, Angebote, Rechnungen
        und Check-in-Links; ohne diese beiden Felder gehen sie unvollständig hinaus.
        Leer bleiben sie zu Recht bei nicht eingetragenen Einzelunternehmern, deshalb
        steht das auch im Hinweis.
      */}
      <div className="grid grid-cols-2 gap-3">
        <Field
          label={de ? 'Firmenbuchnummer' : 'Company number'}
          hint={de ? 'Leer, wenn nicht eingetragen' : 'Leave empty if not registered'}
        >
          <Input value={draft.company_register ?? ''} placeholder="FN 123456 a"
            onChange={(e) => set({ company_register: e.target.value })} />
        </Field>
        <Field
          label={de ? 'Firmenbuchgericht' : 'Register court'}
          hint={de ? 'Gehört zur Nummer' : 'Goes with the number'}
        >
          <Input value={draft.company_register_court ?? ''} placeholder="LG Innsbruck"
            onChange={(e) => set({ company_register_court: e.target.value })} />
        </Field>
      </div>

      {/* Die Steuernummer steht neben ihrer Schwester, der UID (ADR 018): die
          RKSV-Bestellstrecke verlangt eine von beiden, und eine falsche Nummer
          ist dort der teuerste Supportfall (Zertifikatstausch). Geprüft wird
          nur das Format — neun Ziffern, keine Prüfziffer (lib/steuernummer). */}
      <div className="grid grid-cols-2 gap-3">
        <Field label={de ? 'UID-Nummer' : 'VAT ID'}>
          <Input value={draft.vat_id ?? ''} placeholder="ATU12345678"
            onChange={(e) => set({ vat_id: e.target.value })} />
        </Field>
        <Field label={t('steuernummer.label')} hint={t('steuernummer.hint')}>
          <Input value={formatieren(draft.tax_number ?? '')} placeholder="12-345/6789"
            onChange={(e) => { setTaxNumberBad(false); set({ tax_number: e.target.value }) }} />
        </Field>
      </div>
      {taxNumberBad && (
        <p className="text-sm bg-warn-soft text-warn rounded-xl px-3 py-2">
          {t('steuernummer.invalid')}
        </p>
      )}
      <Field label={de ? 'E-Mail' : 'Email'}>
        <Input type="email" value={draft.email ?? ''}
          onChange={(e) => set({ email: e.target.value })} />
      </Field>

      <Field label={de ? 'Umsatzsteuer' : 'VAT scheme'}>
        <Select
          value={draft.vat_scheme}
          onChange={(e) => set({ vat_scheme: e.target.value as Database['tenant']['vat_scheme'] })}
        >
          <option value="standard">
            {de ? 'Regelbesteuerung' : 'Standard VAT'}
          </option>
          <option value="small_business">
            {de ? 'Kleinunternehmer (keine USt.)' : 'Small business (no VAT)'}
          </option>
        </Select>
      </Field>

      {draft.vat_scheme === 'standard' && (
        <Field
          label={de ? 'Preisangaben' : 'How prices are entered'}
          hint={de
            ? 'Gilt für Saisonpreise, Zuschläge, Nebenkosten und Ortstaxe.'
            : 'Applies to seasonal rates, surcharges, fees and the tourist tax.'}
        >
          <Select
            value={draft.prices_include_vat === false ? 'net' : 'gross'}
            onChange={(e) => set({ prices_include_vat: e.target.value === 'gross' })}
          >
            <option value="gross">
              {de ? 'Preise inklusive USt. (Bruttopreise)' : 'Prices include VAT (gross)'}
            </option>
            <option value="net">
              {de ? 'Preise zzgl. USt. (Nettopreise)' : 'Prices exclude VAT (net)'}
            </option>
          </Select>
        </Field>
      )}

      {draft.vat_scheme === 'standard' && (
        <div className="grid grid-cols-2 gap-3">
          <Field label={de ? 'USt. Nächtigung %' : 'VAT accommodation %'}>
            <Input type="number" step="0.1" value={draft.vat_rate_accommodation}
              onChange={(e) => set({ vat_rate_accommodation: Number(e.target.value) })} />
          </Field>
          <Field label={de ? 'USt. Leistungen %' : 'VAT services %'}>
            <Input type="number" step="0.1" value={draft.vat_rate_services}
              onChange={(e) => set({ vat_rate_services: Number(e.target.value) })} />
          </Field>
        </div>
      )}

      {/* Die Anzahlungsregel gehört zu den Rechnungsangaben und nicht in eine eigene
          Karte: sie ist eine Zahlungsbedingung wie die IBAN darunter, und wer beides
          an zwei Stellen sucht, findet keines von beiden. */}
      <Field
        label={de ? 'Anzahlung' : 'Deposit payment'}
        hint={de
          ? 'Vorschlag für jede neue Buchung — in der Buchung bleibt der Betrag änderbar.'
          : 'Suggested on every new booking — still editable per booking.'}
      >
        <Select
          value={draft.prepayment_mode ?? 'percent'}
          onChange={(e) => set({
            prepayment_mode: e.target.value as 'percent' | 'fixed' | 'none',
          })}
        >
          <option value="percent">
            {de ? 'Anteil am Gesamtpreis' : 'Share of the total'}
          </option>
          <option value="fixed">
            {de ? 'Immer derselbe Betrag' : 'Always the same amount'}
          </option>
          <option value="none">
            {de ? 'Keine Anzahlung' : 'No deposit payment'}
          </option>
        </Select>
      </Field>

      {(draft.prepayment_mode ?? 'percent') === 'percent' && (
        <Field label={de ? 'Anteil in Prozent' : 'Share in percent'}>
          <Input type="number" min={0} max={100} step="1"
            value={draft.prepayment_percent ?? 30}
            onChange={(e) => set({ prepayment_percent: Number(e.target.value) })} />
        </Field>
      )}
      {draft.prepayment_mode === 'fixed' && (
        <Field
          label={de ? 'Betrag' : 'Amount'}
          hint={de
            ? 'Bei kürzeren Aufenthalten höchstens der Gesamtpreis.'
            : 'Capped at the total for shorter stays.'}
        >
          <Input type="number" min={0} step="0.01"
            value={draft.prepayment_fixed ?? 0}
            onChange={(e) => set({ prepayment_fixed: Number(e.target.value) })} />
        </Field>
      )}

      <Field label={de ? 'Kontoinhaber' : 'Account holder'}>
        <Input value={draft.bank_holder ?? ''}
          onChange={(e) => set({ bank_holder: e.target.value })} />
      </Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label="IBAN" className="col-span-2">
          <Input value={draft.bank_iban ?? ''} placeholder="AT61 1904 3002 3457 3201"
            onChange={(e) => set({ bank_iban: e.target.value })} />
        </Field>
        <Field label="BIC">
          <Input value={draft.bank_bic ?? ''}
            onChange={(e) => set({ bank_bic: e.target.value })} />
        </Field>
      </div>

      <Button variant="primary" onClick={save}>
        {saved ? <><Check size={16} /> {de ? 'Gespeichert' : 'Saved'}</> : t('common.save')}
      </Button>
    </Card>
  )
}

/**
 * The public enquiry form. Which dates accept enquiries is driven by the seasons the
 * landlord already maintains — peak weeks are usually the ones they want to handle
 * personally, so each season carries its own switch instead of a second calendar.
 */
function EnquiryCard() {
  const { lang, t } = useI18n()
  const { mode } = useAuth()
  const store = useStore()
  const de = lang === 'de'
  const [copied, setCopied] = useState(false)
  const slug = store.db.tenant.slug
  const url = oeffentlicheUrl(`/e/${slug}`)
  const enabled = store.db.tenant.enquiry_enabled ?? false

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="font-medium">{de ? 'Online-Anfragen' : 'Online enquiries'}</h2>
          <p className="text-xs text-muted mt-1">
            {de
              ? 'Gäste fragen direkt über eine eigene Seite an. Die Anfrage landet sofort im Kalender — kein Weiterleiten von E-Mails.'
              : 'Guests enquire through their own page. The enquiry lands straight in the calendar — no forwarding emails.'}
          </p>
        </div>
        <button
          role="switch"
          aria-checked={enabled}
          onClick={() => store.saveTenant({ enquiry_enabled: !enabled })}
          className={cx('w-12 h-7 rounded-full shrink-0 transition-colors relative',
            enabled ? 'bg-accent' : 'bg-border')}
        >
          <span className={cx(
            'absolute top-1 w-5 h-5 rounded-full bg-white transition-all',
            enabled ? 'left-6' : 'left-1')} />
        </button>
      </div>

      {enabled && (
        <>
          <div className="flex gap-2">
            <Input readOnly value={url} className="font-mono text-xs" />
            <Button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(url)
                  setCopied(true)
                  setTimeout(() => setCopied(false), 1500)
                } catch { /* clipboard blocked — the field is selectable anyway */ }
              }}
            >
              {copied ? <Check size={16} /> : (de ? 'Kopieren' : 'Copy')}
            </Button>
          </div>

          {/* Der Demobetrieb steht nur im Browser, nicht auf dem Server. Der Link
              führt deshalb auf eine Vorschau aus den Demodaten — sehenswert, aber
              nicht weitergebbar, und abgeschickt wird dort nichts. */}
          {mode === 'demo' && (
            <p className="text-xs text-muted">
              {de
                ? 'In der Demo öffnet der Link eine Vorschau mit Beispieldaten. Sie sieht aus wie die spätere Seite, nimmt aber keine Anfragen entgegen.'
                : 'In the demo the link opens a preview with sample data. It looks like the real page but accepts no enquiries.'}
            </p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label={de ? 'Vorlaufzeit (Tage)' : 'Lead time (days)'}>
              <Input type="number" min={0} max={365}
                value={store.db.tenant.enquiry_lead_days ?? 0}
                onChange={(e) => store.saveTenant({ enquiry_lead_days: Number(e.target.value) })} />
            </Field>
            <Field label={de ? 'Max. Nächte' : 'Max nights'}>
              <Input type="number" min={1}
                value={store.db.tenant.enquiry_max_nights ?? ''}
                onChange={(e) => store.saveTenant({
                  enquiry_max_nights: e.target.value === '' ? null : Number(e.target.value),
                })} />
            </Field>
          </div>

          <div className="border-t border-border pt-3">
            <p className="text-xs text-muted mb-2">
              {de
                ? 'Welche Saisonen online anfragbar sind. Hochpreis-Zeiten hier abschalten und selbst vergeben.'
                : 'Which seasons accept online enquiries. Switch high-price periods off and handle them yourself.'}
            </p>
            <div className="space-y-2">
              {store.db.rate_periods.map((r) => (
                <label key={r.id} className="flex items-center gap-3 text-sm">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-[var(--color-accent)]"
                    checked={r.online_enquiry !== false}
                    onChange={(e) => store.saveRatePeriod(r.id, {
                      online_enquiry: e.target.checked,
                    })}
                  />
                  <span className="min-w-0 flex-1 truncate">{r.name}</span>
                  <span className="text-xs text-muted whitespace-nowrap">
                    {r.valid_from.slice(5)} – {r.valid_to.slice(5)}
                  </span>
                </label>
              ))}
              <label className="flex items-center gap-3 text-sm border-t border-border pt-2">
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-[var(--color-accent)]"
                  checked={store.db.tenant.enquiry_default_open ?? true}
                  onChange={(e) => store.saveTenant({ enquiry_default_open: e.target.checked })}
                />
                <span className="min-w-0 flex-1">
                  {de ? 'Übrige Zeiträume (ohne Saison)' : 'All other dates (no season)'}
                </span>
              </label>
            </div>
          </div>
          <p className="text-xs text-muted">{t('touristTax.title')} &amp; {t('nav.rates')} →</p>
        </>
      )}
    </Card>
  )
}

/** Municipality rate for the tourist tax. Zero until the landlord sets it. */
function TouristTaxCard() {
  const { lang, t } = useI18n()
  const store = useStore()
  const de = lang === 'de'
  const [draft, setDraft] = useState(store.touristTax)
  const [saved, setSaved] = useState(false)

  useEffect(() => { setDraft(store.touristTax) }, [store.touristTax])

  return (
    <Card className="p-4 space-y-3">
      <h2 className="font-medium">{t('touristTax.title')}</h2>
      <p className="text-xs text-muted">
        {de
          ? 'Von der Gemeinde vorgegeben, nicht vom Vermieter. Sie wird pro Person und Nacht verrechnet und landet in der Ortstaxen-Meldung.'
          : 'Set by the municipality, not by you. Charged per person per night and carried into the tourist-tax return.'}
      </p>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t('touristTax.municipality')}>
          <Input value={draft.municipality}
            onChange={(e) => { setDraft({ ...draft, municipality: e.target.value }); setSaved(false) }} />
        </Field>
        <Field label={`€ ${t('touristTax.perPersonNight')}`}>
          <Input type="number" step="0.01" value={draft.amount_per_person_night}
            onChange={(e) => {
              setDraft({ ...draft, amount_per_person_night: Number(e.target.value) })
              setSaved(false)
            }} />
        </Field>
        <Field label={de ? 'Befreit unter Jahren' : 'Exempt under age'}>
          <Input type="number" value={draft.exempt_under_age ?? ''}
            onChange={(e) => {
              setDraft({
                ...draft,
                exempt_under_age: e.target.value === '' ? null : Number(e.target.value),
              })
              setSaved(false)
            }} />
        </Field>
        <Field label={de ? 'Max. Nächte' : 'Max nights'}>
          <Input type="number" value={draft.max_nights ?? ''}
            onChange={(e) => {
              setDraft({
                ...draft,
                max_nights: e.target.value === '' ? null : Number(e.target.value),
              })
              setSaved(false)
            }} />
        </Field>
      </div>
      <Button
        variant="primary"
        onClick={async () => { await store.saveTouristTax(draft); setSaved(true) }}
      >
        {saved ? <><Check size={16} /> {de ? 'Gespeichert' : 'Saved'}</> : t('common.save')}
      </Button>
    </Card>
  )
}

export default function SettingsView() {
  const { t, lang, setLang } = useI18n()
  const { mode, session, signOut } = useAuth()
  const { rolle } = useAuth()
  const store = useStore()
  const de = lang === 'de'

  return (
    <div className="p-4 pt-safe space-y-4 max-w-3xl">
      <h1 className="text-xl font-semibold">{t('nav.settings')}</h1>

      <IssuerCard />

      <LogoCard />

      <Card className="p-4">
        <p className="text-xs text-muted">
          {store.live
            ? (de ? 'Verbunden mit dem Server' : 'Connected to the server')
            : (de ? 'Demomodus — lokale Beispieldaten' : 'Demo mode — local sample data')}
          {store.loading && ` · ${t('common.loading')}`}
        </p>
      </Card>

      <EnquiryCard />

      <TouristTaxCard />

      <UnitsCard />

      <StripeConnectCard />
      <ComplianceDemoNote />
      <TouristTaxFilingCard />
      <RksvCard />

      <RatesCard />

      {/* Die Kinderermäßigung ist eine Preisregel und gehört zu den Preisen. Sie
          stand zuerst weiter oben bei Logo und Stammdaten — und wurde dort nicht
          gefunden, was der beste Beweis dafür ist, dass sie da nicht hingehört. */}
      <ChildRatesCard />

      {/* Die Stornobedingungen stehen bei den Preisen, weil sie eine
          Verkaufsbedingung sind und keine Rechtsseite: sie entstehen im selben
          Gedanken wie der Preis, und dort sucht der Vermieter sie auch. */}
      <CancellationTermsCard />

      <IcalCard />

      {/* Der KI-Verbrauch steht bei den laufenden Kosten und nicht bei den
          Funktionen: die Frage, die ihn hierher führt, ist „was kostet mich
          das", nicht „was kann das". */}
      <AiUsageCard />

      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-3">
          <span className="text-sm flex-1">Sprache / Language</span>
          <div className="flex gap-1">
            {(['de', 'en'] as const).map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={cx('px-3 py-1.5 rounded-lg text-xs font-medium uppercase',
                  lang === l ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-muted')}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {store.live && (
          <Button onClick={store.reload} disabled={store.loading}>
            <RotateCw size={16} /> {de ? 'Daten neu laden' : 'Reload data'}
          </Button>
        )}
        {mode === 'demo' && (
          <Button variant="danger" onClick={store.reset}>
            <RefreshCw size={16} /> {de ? 'Demodaten zurücksetzen' : 'Reset demo data'}
          </Button>
        )}

        {/* Das Konto-Feld: Kundennummer, Version, die beiden Pflichtseiten und der
            Weg hinaus. Die Kundennummer steht bewusst zuoberst — sie ist das, was
            der Support als Erstes fragt, und sie zu suchen ist niemandes Aufgabe. */}
        <div className="border-t border-border pt-3 space-y-2">
          {/*
            * DER DAUERHAFTE WEG ZUM TARIF.
            *
            * Bis zum 03.08.2026 war `/billing/setup` AUSSCHLIESSLICH über das
            * Testphasen-Band erreichbar. Das Band verschwindet aber, sobald
            * alles in Ordnung ist — wer bezahlt hat, kam also an seinen eigenen
            * Tarif nicht mehr heran. Ausgerechnet der zufriedene Kunde stand vor
            * einer Tür ohne Klinke.
            *
            * `kaufwegSichtbar()`, weil in der App-Hülle kein Weg zu einem Kauf
            * ausserhalb der App führen darf (3.1.3(f), und Google Play kennt
            * dafür gar keine Ausnahme). `darf(rolle, 'abrechnung')`, weil
            * Zahlungsmittel und Abo Chefsache sind — dieselbe Grenze wie in
            * `ai_set_overage_consent` und `BILLING_ROLES`.
            */}
          {kaufwegSichtbar() && darf(rolle, 'abrechnung') && (
            <Link
              to="/billing/setup"
              className="flex items-center justify-between rounded-xl bg-surface-2 px-3 py-2 text-sm"
            >
              <span>{de ? 'Tarif und Zahlungsdaten' : 'Plan and payment details'}</span>
              <span className="text-muted text-xs">
                {de ? 'ansehen' : 'view'}
              </span>
            </Link>
          )}

          <div className="text-xs text-muted space-y-0.5">
            {store.db.tenant.customer_number && (
              <p className="tabular-nums">
                {t('account.customerNumber')}: <span className="font-medium text-fg">
                  {store.db.tenant.customer_number}
                </span>
              </p>
            )}
            {session && <p>{session.user.email}</p>}
            <p className="tabular-nums">StaySuite v{__APP_VERSION__}</p>
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
            <Link to="/datenschutz" className="underline underline-offset-2">
              {t('account.privacyLong')}
            </Link>
            <Link to="/impressum" className="underline underline-offset-2">
              {t('account.imprint')}
            </Link>
            <Link to="/agb" className="underline underline-offset-2">
              {t('account.terms')}
            </Link>
          </div>

          <Button variant="ghost" onClick={signOut}>
            <LogOut size={16} /> {de ? 'Abmelden' : 'Sign out'}
          </Button>

          {/* Konto löschen (Vorgang 019, Apple 5.1.1(v)): im Konto-Block, wo
              auch „Abmelden" wohnt — ein Bildschirm, kein Untermenü (AK12).
              Nur im Live-Modus: die Demo hat kein Konto, dort existiert der
              Eintrag nicht (AK1). */}
          {mode === 'live' && <AvvNachweise />}
          {mode === 'live' && <AboKuendigenEintrag />}
          {mode === 'live' && <KontoLoeschenEintrag />}
        </div>
      </Card>
    </div>
  )
}
